import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

/**
 * Publish targets (Epic O5). A completed, approved run dispatches its outcome to
 * one of these channels. `next_agent` is handled in-run by delegation; the rest
 * are external integrations resolved at publish time.
 */
export const publishProviders = [
  'email',
  'teams',
  'sharepoint',
  'jira',
  'azure_devops',
  'webhook',
  'next_agent',
] as const;
export const publishProviderSchema = z.enum(publishProviders);
export type PublishProvider = z.infer<typeof publishProviderSchema>;

export const publishDeliveryStatuses = ['pending', 'delivered', 'failed', 'skipped'] as const;
export const publishDeliveryStatusSchema = z.enum(publishDeliveryStatuses);
export type PublishDeliveryStatus = z.infer<typeof publishDeliveryStatusSchema>;

/** An immutable record of a publish dispatch attempt, surfaced in the evidence pack. */
export const publishDeliverySchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    runId: idSchema,
    gateId: idSchema.nullish(),
    integrationId: idSchema.nullish(),
    provider: z.string(),
    /** Where the outcome was sent (url / address / project key), for the audit trail. */
    target: z.string().nullish(),
    status: publishDeliveryStatusSchema.default('pending'),
    detail: z.record(z.unknown()).default({}),
  })
  .merge(timestampsSchema);
export type PublishDelivery = z.infer<typeof publishDeliverySchema>;

/**
 * Provider-specific shaping of the outcome into an outbound HTTP body. Kept here
 * (pure) so the API and runtime format deliveries identically.
 */
export function formatPublishPayload(
  provider: string,
  outcome: { runId: string; title: string; body: string },
): Record<string, unknown> {
  switch (provider) {
    case 'teams':
      // Minimal MessageCard the Teams incoming webhook understands.
      return {
        '@type': 'MessageCard',
        '@context': 'https://schema.org/extensions',
        summary: outcome.title,
        title: outcome.title,
        text: outcome.body,
      };
    case 'email':
      return { subject: outcome.title, body: outcome.body, runId: outcome.runId };
    case 'jira':
    case 'azure_devops':
      return { summary: outcome.title, description: outcome.body, runId: outcome.runId };
    case 'sharepoint':
      return { name: outcome.title, content: outcome.body, runId: outcome.runId };
    default:
      return { title: outcome.title, body: outcome.body, runId: outcome.runId };
  }
}
